import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../components/Sidebar';
import { visaProgramAPI, checklistAPI } from '../utils/api';
import {
  Plus, Trash2, Eye, FileText, CheckSquare, Square,
  GripVertical, ChevronDown, ChevronUp, Pencil, X,
  Lightbulb, Search, Check, Copy,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Applicability config ─────────────────────────────────────────────────────

const APP_OPTIONS = [
  { value: 'primary_only',         label: 'Primary Only',         color: 'bg-blue-100 text-blue-700' },
  { value: 'spouse_only',          label: 'Spouse Only',          color: 'bg-pink-100 text-pink-700' },
  { value: 'children_only',        label: 'Children Only',        color: 'bg-yellow-100 text-yellow-700' },
  { value: 'primary_and_spouse',   label: 'Primary + Spouse',     color: 'bg-purple-100 text-purple-700' },
  { value: 'primary_and_children', label: 'Primary + Children',   color: 'bg-indigo-100 text-indigo-700' },
  { value: 'spouse_and_children',  label: 'Spouse + Children',    color: 'bg-rose-100 text-rose-700' },
  { value: 'all_members',          label: 'All Family Members',   color: 'bg-green-100 text-green-700' },
  { value: 'family_shared',        label: 'Family Shared (1×)',   color: 'bg-slate-100 text-slate-700' },
];

// derive applicability string from checkbox state
const buildApplicability = (checks, familyShared) => {
  if (familyShared) return 'family_shared';
  const { primary, spouse, children } = checks;
  if (primary && spouse && children) return 'all_members';
  if (primary && spouse)   return 'primary_and_spouse';
  if (primary && children) return 'primary_and_children';
  if (spouse && children)  return 'spouse_and_children';
  if (spouse)   return 'spouse_only';
  if (children) return 'children_only';
  return 'primary_only';
};

// parse applicability string back into checkbox state
const parseApplicability = (val) => {
  const fs = val === 'family_shared';
  return {
    familyShared: fs,
    checks: {
      primary:  ['primary_only','primary_and_spouse','primary_and_children','all_members'].includes(val),
      spouse:   ['spouse_only','primary_and_spouse','spouse_and_children','all_members'].includes(val),
      children: ['children_only','primary_and_children','spouse_and_children','all_members'].includes(val),
    },
  };
};

function AppBadge({ value }) {
  const opt = APP_OPTIONS.find(o => o.value === value) || APP_OPTIONS[0];
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${opt.color}`}>{opt.label}</span>;
}

// ─── Empty form state ─────────────────────────────────────────────────────────

const EMPTY_ITEM = { name: '', description: '', required: true, allow_multiple: false, upload_by: 'client' };
const EMPTY_CHECKS = { primary: true, spouse: false, children: false };
const EMPTY_SIDES = [{ key: 'front', label: 'Front Page' }, { key: 'back', label: 'Back Page' }];

// ─── Item Form (used for both Add and Edit) ───────────────────────────────────

function ItemForm({ initial, initialChecks, initialFamilyShared, initialMultiSide, initialSides, onSubmit, onCancel, title }) {
  const [item, setItem]               = useState(initial);
  const [checks, setChecks]           = useState(initialChecks);
  const [familyShared, setFamilyShared] = useState(initialFamilyShared);
  const [multiSide, setMultiSide]     = useState(initialMultiSide);
  const [sides, setSides]             = useState(initialSides);

  const toggleCheck = (key) => {
    if (familyShared) return;
    setChecks(p => ({ ...p, [key]: !p[key] }));
  };

  const addSide = () => setSides(p => [...p, { key: `page_${p.length + 1}`, label: '' }]);
  const updateSide = (i, f, v) => setSides(p => p.map((s, idx) => idx === i ? { ...s, [f]: v } : s));
  const removeSide = (i) => setSides(p => p.filter((_, idx) => idx !== i));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!item.name.trim()) return;
    const applicability = buildApplicability(checks, familyShared);
    const per_member = !['primary_only', 'family_shared'].includes(applicability);
    const document_sides = multiSide ? sides.filter(s => s.key && s.label) : [];
    onSubmit({ ...item, applicability, per_member, document_sides });
  };

  const noneChecked = !familyShared && !checks.primary && !checks.spouse && !checks.children;
  const derivedLabel = APP_OPTIONS.find(o => o.value === buildApplicability(checks, familyShared))?.label;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">Document Name *</label>
        <input
          type="text" required
          value={item.name}
          onChange={e => setItem(p => ({ ...p, name: e.target.value }))}
          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm"
          placeholder="e.g. Passport, IELTS Score Card, Bank Statements"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1">Client Instructions <span className="text-slate-400 font-normal">(optional)</span></label>
        <textarea
          rows={2} value={item.description}
          onChange={e => setItem(p => ({ ...p, description: e.target.value }))}
          className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm resize-none"
          placeholder="e.g. Must be valid for at least 6 months from travel date"
        />
      </div>

      {/* Applicability checkboxes */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-slate-700">Applies To *</label>
          {!noneChecked && (
            <span className="text-xs text-slate-500">→ <span className="font-medium text-teal-600">{derivedLabel}</span></span>
          )}
        </div>
        <div className="p-3 bg-white border border-slate-200 rounded-lg space-y-2">
          {/* Three member checkboxes */}
          <div className="flex items-center gap-4">
            {[
              { key: 'primary',  label: 'Primary Applicant', color: 'text-blue-600' },
              { key: 'spouse',   label: 'Spouse',            color: 'text-pink-600' },
              { key: 'children', label: 'Children',          color: 'text-yellow-600' },
            ].map(({ key, label, color }) => (
              <label key={key} className={`flex items-center gap-1.5 cursor-pointer select-none ${familyShared ? 'opacity-40' : ''}`}>
                <input
                  type="checkbox"
                  checked={checks[key]}
                  onChange={() => toggleCheck(key)}
                  disabled={familyShared}
                  className="w-4 h-4 text-teal-500 border-slate-300 rounded"
                />
                <span className={`text-sm font-medium ${color}`}>{label}</span>
              </label>
            ))}
          </div>
          {/* Separator */}
          <div className="border-t border-slate-100 pt-2">
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={familyShared}
                onChange={e => { setFamilyShared(e.target.checked); if (e.target.checked) setChecks(EMPTY_CHECKS); }}
                className="w-4 h-4 text-teal-500 border-slate-300 rounded"
              />
              <span className="text-sm font-medium text-slate-600">Family Shared <span className="text-slate-400 font-normal">(one upload covers the whole family — e.g. marriage certificate)</span></span>
            </label>
          </div>
        </div>
        {noneChecked && (
          <p className="text-xs text-red-500 mt-1">Select at least one applicant or choose Family Shared.</p>
        )}
        {!familyShared && (checks.spouse || checks.children) && (
          <p className="text-xs text-teal-600 mt-1 font-medium">Each selected family member will get their own upload slot.</p>
        )}
      </div>

      {/* Multi-side */}
      <div className="p-3 bg-white border border-slate-200 rounded-lg">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={multiSide} onChange={e => setMultiSide(e.target.checked)} className="w-4 h-4 text-teal-500 border-slate-300 rounded" />
          <span className="text-sm font-semibold text-slate-700">Multi-side document</span>
          <span className="text-xs text-slate-400">(e.g. passport front &amp; back)</span>
        </label>
        {multiSide && (
          <div className="mt-3 space-y-2 pl-6">
            {sides.map((side, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="text" placeholder="key" value={side.key} onChange={e => updateSide(i, 'key', e.target.value)}
                  className="w-20 px-2 py-1 text-xs border border-slate-200 rounded focus:ring-1 focus:ring-teal-500 outline-none" />
                <input type="text" placeholder="Label shown to client" value={side.label} onChange={e => updateSide(i, 'label', e.target.value)}
                  className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded focus:ring-1 focus:ring-teal-500 outline-none" />
                {sides.length > 1 && (
                  <button type="button" onClick={() => removeSide(i)}><X size={12} className="text-red-400 hover:text-red-600" /></button>
                )}
              </div>
            ))}
            <button type="button" onClick={addSide} className="text-teal-600 text-xs hover:text-teal-800 font-medium">+ Add side</button>
          </div>
        )}
      </div>

      {/* Who uploads */}
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">Uploaded By *</label>
        <div className="flex gap-3">
          {[
            { value: 'client',  label: 'Client',       desc: 'Client uploads from their portal',        color: 'border-teal-500 bg-teal-50' },
            { value: 'officer', label: 'Case Officer',  desc: 'Officer prepares & uploads; client reviews', color: 'border-indigo-500 bg-indigo-50' },
          ].map(opt => (
            <label key={opt.value} className={`flex-1 flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${item.upload_by === opt.value ? opt.color : 'border-slate-200 bg-white hover:border-slate-300'}`}>
              <input type="radio" name="upload_by" value={opt.value} checked={item.upload_by === opt.value} onChange={e => setItem(p => ({ ...p, upload_by: e.target.value }))} className="mt-0.5 text-teal-500" />
              <div>
                <p className="text-sm font-semibold text-slate-800">{opt.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Flags */}
      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={item.required} onChange={e => setItem(p => ({ ...p, required: e.target.checked }))} className="w-4 h-4 text-teal-500 border-slate-300 rounded" />
          <span className="text-sm font-medium text-slate-700">Required</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={item.allow_multiple} onChange={e => setItem(p => ({ ...p, allow_multiple: e.target.checked }))} className="w-4 h-4 text-teal-500 border-slate-300 rounded" />
          <span className="text-sm font-medium text-slate-700">Allow multiple uploads</span>
          <span className="text-xs text-slate-400">(bank statements, etc.)</span>
        </label>
      </div>

      <div className="flex gap-3 pt-1">
        <button type="submit" disabled={noneChecked} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">{title}</button>
        <button type="button" onClick={onCancel} className="px-5 py-2 bg-slate-200 text-slate-700 rounded-lg font-semibold hover:bg-slate-300 transition-all text-sm">Cancel</button>
      </div>
    </form>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminChecklistTemplates() {
  const [programs, setPrograms]               = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [checklist, setChecklist]             = useState(null);
  const [showClientPreview, setShowClientPreview] = useState(false);
  const [loading, setLoading]                 = useState(true);
  const [draggedIndex, setDraggedIndex]       = useState(null);
  const [expandedItem, setExpandedItem]       = useState(null);

  // add / edit
  const [formMode, setFormMode]     = useState(null); // null | 'add' | 'edit'
  const [editingIndex, setEditingIndex] = useState(null);

  // suggestions
  const [allTemplates, setAllTemplates]   = useState([]);
  const [suggSearch, setSuggSearch]       = useState('');
  const [showSugg, setShowSugg]           = useState(true);

  useEffect(() => { fetchPrograms(); }, []); // eslint-disable-line

  const fetchPrograms = async () => {
    try {
      const [progRes, allRes] = await Promise.allSettled([
        visaProgramAPI.getPrograms(),
        checklistAPI.getAllTemplates(),
      ]);
      if (progRes.status === 'fulfilled') {
        const progs = progRes.value.data;
        setPrograms(progs);
        if (progs.length > 0) { setSelectedProgram(progs[0]); fetchChecklist(progs[0].id); }
      }
      if (allRes.status === 'fulfilled') setAllTemplates(allRes.value.data || []);
    } catch {
      toast.error('Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklist = async (programId) => {
    try {
      const res = await checklistAPI.getChecklistByProgram(programId);
      setChecklist(res.data);
    } catch {
      setChecklist(null);
    }
    // also refresh all templates for suggestions
    try {
      const res = await checklistAPI.getAllTemplates();
      setAllTemplates(res.data || []);
    } catch { /* ignore */ }
  };

  const handleProgramChange = (program) => {
    setSelectedProgram(program);
    fetchChecklist(program.id);
    setFormMode(null);
    setExpandedItem(null);
  };

  const saveItems = async (items) => {
    await checklistAPI.createTemplate({ visa_program_id: selectedProgram.id, items });
    await fetchChecklist(selectedProgram.id);
  };

  // ── Add
  const handleAdd = async (builtItem) => {
    try {
      await saveItems([...(checklist?.items || []), builtItem]);
      toast.success('Document added to checklist');
      setFormMode(null);
    } catch { toast.error('Failed to add item'); }
  };

  // ── Edit
  const openEdit = (index) => {
    setEditingIndex(index);
    setFormMode('edit');
    setExpandedItem(null);
  };

  const handleEdit = async (builtItem) => {
    const items = [...(checklist?.items || [])];
    items[editingIndex] = builtItem;
    try {
      await saveItems(items);
      toast.success('Document updated');
      setFormMode(null);
      setEditingIndex(null);
    } catch { toast.error('Failed to update item'); }
  };

  // ── Delete
  const handleDelete = async (index) => {
    if (!window.confirm('Delete this checklist item?')) return;
    try {
      await saveItems(checklist.items.filter((_, i) => i !== index));
      toast.success('Item deleted');
      if (editingIndex === index) setFormMode(null);
    } catch { toast.error('Failed to delete item'); }
  };

  // ── Drag-reorder
  const handleDragStart = (e, i) => { setDraggedIndex(i); e.dataTransfer.effectAllowed = 'move'; e.target.style.opacity = '0.5'; };
  const handleDragEnd   = (e) => { e.target.style.opacity = '1'; setDraggedIndex(null); };
  const handleDragOver  = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    const items = [...checklist.items];
    const [d] = items.splice(draggedIndex, 1);
    items.splice(dropIndex, 0, d);
    setChecklist({ ...checklist, items });
    try { await saveItems(items); } catch { fetchChecklist(selectedProgram.id); }
    setDraggedIndex(null);
  };

  // ── Suggestions pool: all items from OTHER programs, sorted by frequency
  const suggestions = useMemo(() => {
    const currentProgramId = selectedProgram?.id;
    const currentNames = new Set((checklist?.items || []).map(i => i.name.toLowerCase().trim()));
    const pool = {};
    allTemplates.forEach(template => {
      if (template.visa_program_id === currentProgramId) return;
      (template.items || []).forEach(item => {
        const key = item.name.toLowerCase().trim();
        if (!pool[key]) pool[key] = { item, count: 0 };
        pool[key].count++;
      });
    });
    return Object.values(pool)
      .sort((a, b) => b.count - a.count)
      .map(({ item, count }) => ({ ...item, _count: count, _added: currentNames.has(item.name.toLowerCase().trim()) }));
  }, [allTemplates, selectedProgram, checklist]);

  const filteredSugg = useMemo(() => {
    if (!suggSearch.trim()) return suggestions;
    const t = suggSearch.toLowerCase();
    return suggestions.filter(s => s.name.toLowerCase().includes(t));
  }, [suggestions, suggSearch]);

  const addFromSuggestion = async (suggItem) => {
    const { _count, _added, ...item } = suggItem; // eslint-disable-line no-unused-vars
    if (_added) return;
    try {
      await saveItems([...(checklist?.items || []), item]);
      toast.success(`"${item.name}" added to checklist`);
    } catch { toast.error('Failed to add suggestion'); }
  };

  // ── Edit form initial state
  const editItem = editingIndex !== null ? checklist?.items[editingIndex] : null;
  const editParsed = editItem ? parseApplicability(editItem.applicability || 'primary_only') : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="admin-checklist-templates-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Document Checklist Templates</h1>
            <p className="text-slate-600">Build per-visa-program checklists with full family-member and multi-side support</p>
          </div>
          <button onClick={() => setShowClientPreview(!showClientPreview)} data-testid="preview-client-view-button" className="btn-secondary flex items-center gap-2">
            <Eye size={20} />
            {showClientPreview ? 'Edit Mode' : 'Preview Client View'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* ── Program list ── */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Visa Programs</h2>
              <div className="space-y-2">
                {programs.map(program => (
                  <button key={program.id} onClick={() => handleProgramChange(program)} data-testid={`program-${program.id}`}
                    className={`w-full text-left p-3 rounded-lg transition-all ${selectedProgram?.id === program.id ? 'bg-teal-50 border-2 border-teal-500 text-teal-900' : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'}`}>
                    <p className="font-semibold text-sm">{program.visa_type}</p>
                    <p className="text-xs text-slate-600">{program.country}</p>
                    {/* item count badge */}
                    {(() => { const t = allTemplates.find(t => t.visa_program_id === program.id); return t?.items?.length > 0 ? <span className="text-xs text-teal-600 font-medium">{t.items.length} docs</span> : null; })()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Main panel ── */}
          <div className="lg:col-span-3 space-y-6">
            {showClientPreview ? (
              /* CLIENT PREVIEW */
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 font-semibold">Client Portal Preview — documents are grouped by applicant in the real portal</p>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-1">Document Checklist</h2>
                <p className="text-slate-600 mb-6">{selectedProgram?.visa_type} — {selectedProgram?.country}</p>
                {!checklist || checklist.items.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                    <button onClick={() => setShowClientPreview(false)} className="btn-primary">Add Items</button>
                  </div>
                ) : (
                  APP_OPTIONS.map(opt => {
                    const sec = checklist.items.filter(i => (i.applicability || 'primary_only') === opt.value);
                    if (!sec.length) return null;
                    return (
                      <div key={opt.value} className="mb-5">
                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">{opt.label}</h3>
                        <div className="space-y-2">
                          {sec.map((item, i) => (
                            <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-200 flex items-start justify-between">
                              <div>
                                <p className="font-medium text-slate-900">{item.name}{item.required && <span className="text-red-500 ml-1">*</span>}</p>
                                {item.description && <p className="text-sm text-slate-500 mt-0.5">{item.description}</p>}
                                {item.document_sides?.length > 0 && <p className="text-xs text-slate-400 mt-1">Sides: {item.document_sides.map(s => s.label).join(', ')}</p>}
                              </div>
                              <button className="px-3 py-1.5 bg-teal-500 text-white rounded-lg text-sm font-medium ml-4">Upload</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <>
                {/* ── EDIT MODE — header + form + list ── */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">{selectedProgram?.name}</h2>
                      <p className="text-slate-600">{selectedProgram?.visa_type} — {selectedProgram?.country}</p>
                    </div>
                    {formMode === null && (
                      <button onClick={() => setFormMode('add')} data-testid="add-item-button" className="btn-primary flex items-center gap-2">
                        <Plus size={18} /> Add Document
                      </button>
                    )}
                  </div>

                  {/* Add form */}
                  {formMode === 'add' && (
                    <div className="mb-6 p-5 bg-teal-50 border border-teal-200 rounded-xl">
                      <h3 className="font-bold text-slate-900 mb-4">New Document</h3>
                      <ItemForm
                        initial={{ ...EMPTY_ITEM }}
                        initialChecks={{ ...EMPTY_CHECKS }}
                        initialFamilyShared={false}
                        initialMultiSide={false}
                        initialSides={[...EMPTY_SIDES]}
                        onSubmit={handleAdd}
                        onCancel={() => setFormMode(null)}
                        title="Add to Checklist"
                      />
                    </div>
                  )}

                  {/* Edit form */}
                  {formMode === 'edit' && editItem && (
                    <div className="mb-6 p-5 bg-amber-50 border border-amber-200 rounded-xl">
                      <h3 className="font-bold text-slate-900 mb-1">Edit Document</h3>
                      <p className="text-xs text-amber-600 mb-4">Editing: <span className="font-semibold">{editItem.name}</span></p>
                      <ItemForm
                        initial={{ name: editItem.name, description: editItem.description || '', required: editItem.required ?? true, allow_multiple: editItem.allow_multiple ?? false, upload_by: editItem.upload_by ?? 'client' }}
                        initialChecks={editParsed.checks}
                        initialFamilyShared={editParsed.familyShared}
                        initialMultiSide={(editItem.document_sides?.length ?? 0) > 0}
                        initialSides={editItem.document_sides?.length > 0 ? editItem.document_sides : [...EMPTY_SIDES]}
                        onSubmit={handleEdit}
                        onCancel={() => { setFormMode(null); setEditingIndex(null); }}
                        title="Save Changes"
                      />
                    </div>
                  )}

                  {/* Item list */}
                  {!checklist || checklist.items.length === 0 ? (
                    <div className="text-center py-12">
                      <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500 mb-1">No documents yet</p>
                      <p className="text-sm text-slate-400">Click "Add Document" above or pick from suggestions below</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-slate-500 mb-3">{checklist.items.length} document(s) — drag to reorder</p>
                      {checklist.items.map((item, index) => (
                        <div key={index} draggable
                          onDragStart={e => handleDragStart(e, index)}
                          onDragEnd={handleDragEnd} onDragOver={handleDragOver}
                          onDrop={e => handleDrop(e, index)}
                          className={`rounded-xl border-2 transition-all ${draggedIndex === index ? 'border-teal-400 bg-teal-50' : editingIndex === index ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50 hover:shadow-sm'}`}
                          data-testid={`checklist-item-${index}`}
                        >
                          <div className="flex items-center justify-between p-4 cursor-move">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <GripVertical size={18} className="text-slate-400 shrink-0" />
                              {item.required ? <CheckSquare size={18} className="text-teal-500 shrink-0" /> : <Square size={18} className="text-slate-400 shrink-0" />}
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <p className="font-semibold text-slate-900 text-sm">{item.name}{item.required && <span className="text-red-500 ml-0.5">*</span>}</p>
                                  <AppBadge value={item.applicability || 'primary_only'} />
                                  {item.document_sides?.length > 0 && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">{item.document_sides.length} sides</span>}
                                  {item.allow_multiple && <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-medium">multi</span>}
                                {item.upload_by === 'officer' && <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">Officer uploads</span>}
                                </div>
                                {item.description && <p className="text-xs text-slate-400 mt-0.5 truncate">{item.description}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 ml-2 shrink-0">
                              <button onClick={() => setExpandedItem(expandedItem === index ? null : index)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200">
                                {expandedItem === index ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                              </button>
                              <button onClick={() => openEdit(index)} className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-lg transition-all" title="Edit">
                                <Pencil size={15} />
                              </button>
                              <button onClick={() => handleDelete(index)} data-testid={`delete-item-${index}`} className="p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all">
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </div>
                          {expandedItem === index && (
                            <div className="px-4 pb-4 border-t border-slate-200 pt-3 space-y-1 text-xs text-slate-500">
                              <p><span className="font-semibold text-slate-700">Applies to:</span> {APP_OPTIONS.find(o => o.value === (item.applicability || 'primary_only'))?.label}</p>
                              <p><span className="font-semibold text-slate-700">Per member:</span> {item.per_member ? 'Yes — each person uploads separately' : 'No'}</p>
                              {item.document_sides?.length > 0 && <p><span className="font-semibold text-slate-700">Sides:</span> {item.document_sides.map(s => s.label).join(' · ')}</p>}
                              <p><span className="font-semibold text-slate-700">Multiple uploads:</span> {item.allow_multiple ? 'Yes' : 'No'}</p>
                              {item.description && <p><span className="font-semibold text-slate-700">Instructions:</span> {item.description}</p>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── SUGGESTION PANEL ── */}
                {suggestions.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-all"
                      onClick={() => setShowSugg(p => !p)}
                    >
                      <div className="flex items-center gap-3">
                        <Lightbulb size={20} className="text-amber-500" />
                        <div className="text-left">
                          <p className="font-bold text-slate-900">Suggested Documents</p>
                          <p className="text-xs text-slate-500">
                            {suggestions.filter(s => !s._added).length} available from {allTemplates.filter(t => t.visa_program_id !== selectedProgram?.id).length} other visa programs
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400">{showSugg ? 'hide' : 'show'}</span>
                        {showSugg ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
                      </div>
                    </button>

                    {showSugg && (
                      <div className="border-t border-slate-100">
                        {/* Search */}
                        <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2">
                          <Search size={15} className="text-slate-400" />
                          <input
                            type="text" value={suggSearch} onChange={e => setSuggSearch(e.target.value)}
                            placeholder="Search suggestions…"
                            className="flex-1 text-sm outline-none bg-transparent text-slate-700 placeholder-slate-400"
                          />
                          {suggSearch && <button onClick={() => setSuggSearch('')}><X size={13} className="text-slate-400 hover:text-slate-600" /></button>}
                        </div>

                        {/* List */}
                        <div className="max-h-80 overflow-y-auto divide-y divide-slate-50">
                          {filteredSugg.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-6">No matching suggestions</p>
                          ) : filteredSugg.map((sugg, i) => (
                            <div key={i} className={`flex items-center justify-between px-6 py-3 hover:bg-slate-50 transition-all ${sugg._added ? 'opacity-50' : ''}`}>
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <Copy size={14} className="text-slate-400 mt-0.5 shrink-0" />
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <p className="text-sm font-semibold text-slate-800">{sugg.name}</p>
                                    <AppBadge value={sugg.applicability || 'primary_only'} />
                                    {sugg.document_sides?.length > 0 && (
                                      <span className="text-xs text-orange-600 font-medium">{sugg.document_sides.map(s => s.label).join(' + ')}</span>
                                    )}
                                    <span className="text-xs text-slate-400">used in {sugg._count} program{sugg._count > 1 ? 's' : ''}</span>
                                  </div>
                                  {sugg.description && <p className="text-xs text-slate-400 mt-0.5 truncate">{sugg.description}</p>}
                                </div>
                              </div>
                              <div className="ml-4 shrink-0">
                                {sugg._added ? (
                                  <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                                    <Check size={13} /> Added
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => addFromSuggestion(sugg)}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-teal-500 text-white rounded-lg text-xs font-semibold hover:bg-teal-600 transition-all"
                                  >
                                    <Plus size={12} /> Add
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
